import { useRef, useMemo, useCallback } from "react";
import type { ConfiguratorSharedProps } from "../../widgetDefinition";
import { Typography, Popover } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import type { TitleComponentSpec } from "../../Processing/types";
import { useSlicerVariables } from "../../Contexts/dashboardHooks";
import { resolveTemplate, saveBlockingWarnings } from "../../Utils/resolveTemplate";
import { VariableSyntaxHelp } from "../shared/VariableSyntaxHelp";
import { SlicerTokenInput, type SlicerTokenInputHandle } from "../shared/SlicerTokenInput";
import { useConfiguratorSync } from "../shared/useConfiguratorSync";
import {
  titleReducer,
  createInitialState,
  hydrateFromSpec,
  formStateToSpec,
  type TitleFormState,
} from "./titleState";

const { Text, Title } = Typography;


export function TitleConfigurator({
  onSpecChange,
  existingSpec,
  metrics,
  getSubjectEntities,
}: ConfiguratorSharedProps<TitleComponentSpec>) {
  const slicerValues = useSlicerVariables();
  // Save stays disabled while a token names a key or a variable the backend rejects.
  const toSpec = useCallback(
    (s: TitleFormState) =>
      saveBlockingWarnings(s.title, slicerValues).length > 0 ? null : formStateToSpec(s),
    [slicerValues],
  );
  const [state, dispatch] = useConfiguratorSync({
    reducer: titleReducer,
    existingSpec,
    hydrate: hydrateFromSpec,
    createInitial: createInitialState,
    toSpec,
    onSpecChange,
    metrics,
    getSubjectEntities,
  });

  const inputRef = useRef<SlicerTokenInputHandle>(null);

  const resolved = useMemo(
    () => resolveTemplate(state.title, slicerValues).resolved,
    [state.title, slicerValues],
  );

  return (
    <div className="awm-simple-configurator">
      <div className="awm-simple-form">
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Text strong style={{ fontSize: 13 }}>Title Text</Text>
          <Popover
            content={<VariableSyntaxHelp onInsert={(tokenText) => inputRef.current?.insert(tokenText)} />}
            title={null}
            trigger="click"
            placement="rightTop"
          >
            <QuestionCircleOutlined style={{ fontSize: 13, color: "#aaa", cursor: "pointer" }} />
          </Popover>
        </div>
        <SlicerTokenInput
          ref={inputRef}
          size="large"
          placeholder="e.g. {CurrentMonth} Overview"
          value={state.title}
          onChange={(title) => dispatch({ type: "SET_TITLE", title })}
          autoFocus
          maxLength={200}
          hint={
            <>
              Type {"{Slicer."} to quote a slicer's dates or athletes. Click <QuestionCircleOutlined /> for every variable.
            </>
          }
        />
      </div>

      <div className="awm-simple-preview">
        <Text
          type="secondary"
          style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}
        >
          Preview
        </Text>
        <div className="awm-title-preview-box">
          <Title level={3} style={{ margin: 0, color: resolved && resolved !== "{}" ? "#1a1a1a" : "#bbb" }}>
            {resolved || "Title Preview"}
          </Title>
        </div>
      </div>
    </div>
  );
}
